/**
 * Skeleton Component
 * 
 * A versatile skeleton loader component for displaying loading states
 * with various shapes and animations.
 */
import { cn } from '../../utils/tailwind-utils'

interface SkeletonProps {
  /**
   * The visual variant of the skeleton
   * @default "default"
   */
  variant?: 'default' | 'circle' | 'rounded'
  
  /**
   * Width of the skeleton
   * Can be a number (px), string with unit, or Tailwind class
   */
  width?: number | string
  
  /**
   * Height of the skeleton
   * Can be a number (px), string with unit, or Tailwind class
   */
  height?: number | string
  
  /**
   * Additional CSS classes
   */
  className?: string
  
  /**
   * Whether to show a shimmer animation
   * @default true
   */
  animate?: boolean
}

/**
 * Skeleton component for displaying loading states
 * 
 * @example
 * // Basic usage
 * <Skeleton width={200} height={20} />
 * 
 * @example
 * // Circle variant for avatars
 * <Skeleton variant="circle" width={40} height={40} />
 * 
 * @example
 * // Using Tailwind classes for dimensions
 * <Skeleton width="w-full" height="h-32" className="my-4" />
 * 
 * @example
 * // Without animation
 * <Skeleton width={150} height={150} animate={false} />
 */
function Skeleton({
  variant = 'default',
  width,
  height,
  className,
  animate = true,
}: SkeletonProps) {
  // Convert number values to pixel strings
  const widthValue = typeof width === 'number' ? `${width}px` : width
  const heightValue = typeof height === 'number' ? `${height}px` : height
  
  // Determine if width/height are Tailwind classes or direct CSS values
  const isTailwindWidth = typeof widthValue === 'string' && widthValue.startsWith('w-')
  const isTailwindHeight = typeof heightValue === 'string' && heightValue.startsWith('h-')
  
  // Style object for direct CSS values
  const style: React.CSSProperties = {}
  
  if (widthValue && !isTailwindWidth) {
    style.width = widthValue
  }
  
  if (heightValue && !isTailwindHeight) {
    style.height = heightValue
  }
  
  // Variant classes
  const variantClasses = {
    default: 'rounded-md',
    circle: 'rounded-full',
    rounded: 'rounded-lg',
  }
  
  return (
    <div
      className={cn(
        'bg-muted/60',
        variantClasses[variant],
        animate && 'animate-pulse',
        isTailwindWidth && widthValue,
        isTailwindHeight && heightValue,
        className
      )}
      style={style}
      aria-hidden="true"
      role="status"
      aria-label="Loading"
    />
  )
}

/**
 * SkeletonText component for displaying text loading states
 * 
 * @example
 * // Basic usage
 * <SkeletonText lines={3} />
 * 
 * @example
 * // With varying line widths
 * <SkeletonText lines={4} lastLineWidth="60%" />
 */
interface SkeletonTextProps {
  /**
   * Number of lines to display
   * @default 1
   */
  lines?: number
  
  /**
   * Width of each line
   * @default "100%"
   */
  lineWidth?: number | string
  
  /**
   * Width of the last line (to create a ragged edge effect)
   * @default "70%"
   */
  lastLineWidth?: number | string
  
  /**
   * Height of each line
   * @default 16
   */
  lineHeight?: number | string
  
  /**
   * Gap between lines
   * @default "0.5rem"
   */
  gap?: number | string
  
  /**
   * Additional CSS classes
   */
  className?: string
  
  /**
   * Whether to show a shimmer animation
   * @default true
   */
  animate?: boolean
}

function SkeletonText({
  lines = 1,
  lineWidth = '100%',
  lastLineWidth = '70%',
  lineHeight = 16,
  gap = '0.5rem',
  className,
  animate = true,
}: SkeletonTextProps) {
  return (
    <div 
      className={cn('flex flex-col', className)}
      style={{ gap: typeof gap === 'number' ? `${gap}px` : gap }}
      role="status"
      aria-label="Loading text"
    >
      {Array.from({ length: lines }).map((_, index) => (
        <Skeleton
          key={index}
          width={index === lines - 1 && lines > 1 ? lastLineWidth : lineWidth}
          height={lineHeight}
          animate={animate}
        />
      ))}
    </div>
  )
}

// Export both components
export { Skeleton, SkeletonText }
export default Skeleton
